import { contactSchema, type Contact } from '@forecastify/shared';
import type { Translate } from '../../i18n';
import type { useSession } from '../../session';
import { parseList } from '../permissions-hook';
import { contactRefusal } from '../deals/contact-dialog';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 21 Oct 2026 · a client's contacts, edited in the client's own form.       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * They were a second tab beside a *Kapcsolattartó* field on the first — two places for one fact.
 * The product owner: one form, a *Kapcsolattartók* section with a row to start and `+` for more.
 * The rows are a draft until the client is saved; then this writes the difference — new rows
 * created, changed ones updated, removed ones deleted — because a new client has no id to hang a
 * contact on until it exists. **The first row is the primary contact**, and the client's own
 * `contactName` / `contactEmail` (which the lists show) are kept equal to it.
 */

/** One row of the form. `id` is null until it is saved. */
export interface ContactRow {
  readonly key: string;
  readonly id: string | null;
  readonly version: number | null;
  readonly name: string;
  readonly email: string;
  readonly phone: string;
  readonly title: string;
}

type Session = ReturnType<typeof useSession>;

let next = 0;
/** A blank row, keyed for React rather than by an id it does not have yet. */
export function blankContact(seed: { name?: string; email?: string } = {}): ContactRow {
  next += 1;
  return {
    key: `new-${String(next)}`,
    id: null,
    version: null,
    name: seed.name ?? '',
    email: seed.email ?? '',
    phone: '',
    title: '',
  };
}

export function rowOf(contact: Contact): ContactRow {
  return {
    key: contact.id,
    id: contact.id,
    version: contact.version,
    name: contact.name,
    email: contact.email ?? '',
    phone: contact.phone,
    title: contact.title,
  };
}

/** A row nobody typed into — dropped on save rather than refused. */
export function isBlank(row: ContactRow): boolean {
  return [row.name, row.email, row.phone, row.title].every((value) => value.trim() === '');
}

/** The client's contacts, primary first; `null` when they could not be read. */
export async function loadContacts(
  session: Session,
  clientId: string,
): Promise<readonly Contact[] | null> {
  const response = await session
    .request(`/contacts?client=${encodeURIComponent(clientId)}`)
    .catch(() => null);
  if (response?.ok !== true) return null;
  return parseList(contactSchema, await response.json());
}

/**
 * Writes the rows against what the client had. Resolves to a refusal sentence, or `null` when
 * every write landed. ⚠️ The client itself is already saved by then; a refusal here says so.
 */
export async function saveContacts(
  session: Session,
  t: Translate,
  clientId: string,
  before: readonly Contact[],
  rows: readonly ContactRow[],
): Promise<string | null> {
  const kept = rows.filter((row) => !isBlank(row));
  const send = async (path: string, init: RequestInit): Promise<string | null> => {
    const response = await session.request(path, init).catch(() => null);
    if (response === null) return t('error.unavailable');
    return response.ok ? null : contactRefusal(response, t);
  };

  /* Deletions first, so a primary that moved is never two contacts at once. */
  for (const old of before) {
    if (kept.some((row) => row.id === old.id)) continue;
    const refused = await send(`/contacts/${old.id}`, { method: 'DELETE' });
    if (refused !== null) return refused;
  }
  for (const [index, row] of kept.entries()) {
    const body = {
      clientId,
      name: row.name.trim(),
      email: row.email.trim() === '' ? null : row.email.trim(),
      phone: row.phone.trim(),
      title: row.title.trim(),
      isPrimary: index === 0,
    };
    if (row.id === null) {
      const refused = await send('/contacts', { method: 'POST', body: JSON.stringify(body) });
      if (refused !== null) return refused;
      continue;
    }
    const old = before.find((contact) => contact.id === row.id);
    /* An unknown old row compares unequal on its name, so it is written. */
    const same =
      old?.name === body.name &&
      (old.email ?? null) === body.email &&
      old.phone === body.phone &&
      old.title === body.title &&
      old.isPrimary === body.isPrimary;
    if (same) continue;
    const refused = await send(`/contacts/${row.id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...body, version: row.version ?? old?.version }),
    });
    if (refused !== null) return refused;
  }
  return null;
}
